import React, { type ReactNode } from 'react';
import {
	type CertificationPeriodSuggestionViewModel,
	getCertificationPeriodSuggestedFrequenciesOptions,
	type FullOrderViewModel,
	getGoalsAndInterventionsByCertificationPeriodOptions,
	getGoalAndInterventionDefinitionLibraryOptions
} from '@generated';
import type { AxiosError } from 'axios';
import type { ProblemDetails } from '~/util/http/types';
import { usePrefetchQuery, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { convertClientDateToServerDate } from '~/util/date';
import { useGetEvaluationOrderOptions } from '~/features/orders/queries';

interface VisitEvaluationProviderContextValue {
	isLoading: boolean;
	fullOrder: FullOrderViewModel;
	suggestedFrequencyPlan?: CertificationPeriodSuggestionViewModel;
	errorRetrievingSuggestedFrequencies: AxiosError<ProblemDetails, unknown> | null;
}

const VisitEvaluationContext = React.createContext<VisitEvaluationProviderContextValue | null>(null);

export function VisitEvaluationProvider({ visitId, children }: { visitId: string; children: ReactNode }): ReactNode {
	const { data: fullOrder } = useSuspenseQuery(useGetEvaluationOrderOptions(visitId));
	const {
		data: suggestedFrequencyPlan,
		isLoading: suggestedFrequenciesLoading,
		error: errorRetrievingSuggestedFrequencies
	} = useQuery({
		...getCertificationPeriodSuggestedFrequenciesOptions(fullOrder.certificationPeriodId),
		enabled: fullOrder !== undefined
	});

	usePrefetchQuery(getGoalsAndInterventionsByCertificationPeriodOptions(fullOrder.certificationPeriodId));

	usePrefetchQuery(getGoalAndInterventionDefinitionLibraryOptions(convertClientDateToServerDate(fullOrder.certificationPeriod.startDate)));

	return (
		<VisitEvaluationContext.Provider
			value={{
				isLoading: suggestedFrequenciesLoading,
				fullOrder,
				suggestedFrequencyPlan: suggestedFrequencyPlan ?? undefined,
				errorRetrievingSuggestedFrequencies
			}}
		>
			{children}
		</VisitEvaluationContext.Provider>
	);
}

export function useVisitEvaluation(): VisitEvaluationProviderContextValue | null {
	return React.useContext(VisitEvaluationContext as React.Context<VisitEvaluationProviderContextValue | null>);
}
